import { useState, useEffect } from "react";
import { track, trackOnce } from "../lib/analytics";
import { openExternal } from "../lib/open-external";
import { ExternalLink, X } from "lucide-react";
import { T } from "../tokens";
import { Badge }    from "../components/Badge";
import { Btn }      from "../components/Btn";
import { ScoreRing } from "../components/ScoreRing";
import { useMatches, useApplications, useUpdateApplication, useDeleteApplication, type TrackedApplication } from "../hooks/useMatches";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useReveal } from "../hooks/useMotion";
import axios from "axios";
import type { MatchWithJob, ApplicationStatus } from "@jobreel/shared";

// The real "I applied myself, now track it" pipeline. Saved/skipped/pending are
// NOT applications-in-progress, so they never show in the tracked list.
const PIPELINE: { status: ApplicationStatus; label: string }[] = [
  { status: "applied",      label: "Applied" },
  { status: "interviewing", label: "Interview" },
  { status: "offer",        label: "Offer" },
  { status: "rejected",     label: "Rejected" },
];
const TRACKED_STATUSES = new Set<ApplicationStatus>([
  "applied", "auto-applied", "interviewing", "offer", "rejected",
]);
const STATUS_VARIANT: Record<string, "info" | "teal" | "ok" | "err" | "warn" | "default"> = {
  "applied":        "info",
  "auto-applied":   "warn",
  "interviewing":   "teal",
  "offer":          "ok",
  "rejected":       "err",
  "pending-review": "default",
};

const STATUS_OPTIONS = [
  { label: "Interested",  value: "saved" },
  { label: "Applied",     value: "applied" },
  { label: "Interview",   value: "interviewing" },
  { label: "Offer",       value: "offer" },
  { label: "Rejected",    value: "rejected" },
];

function openPortal(url?: string | null) {
  openExternal(url);
}

// A row shell: whole row opens the portal on click; the action cluster inside
// stops propagation so its own buttons don't also trigger the row.
// Uses column layout so the info and button rows stack — this prevents the
// three action buttons from crushing the title to a single character on mobile.
function Row({ url, index, children }: { url?: string | null; index: number; children: React.ReactNode }) {
  const [h, setH] = useState(false);
  const rev = useReveal(index * 50, 8);
  const clickable = !!url;
  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={() => openPortal(url)}
      style={{
        ...rev,
        display: "flex", flexDirection: "column", gap: "10px",
        background: h ? T.s2 : T.s1, border: `1px solid ${h ? T.b2 : T.b0}`,
        borderRadius: "10px", padding: "14px 16px",
        transition: "background 0.15s ease, border-color 0.15s ease",
        cursor: clickable ? "pointer" : "default",
      }}
    >
      {children}
    </div>
  );
}

function Actions({ children }: { children: React.ReactNode }) {
  return <div onClick={(e) => e.stopPropagation()}>{children}</div>;
}

// ── Tracked row — an application you've already logged; advance its status ────
// STACKED (not the horizontal Row): the right column is narrow, so title/company
// gets its own full-width line on top and the status pipeline + Open sit below —
// otherwise the 4 status badges crush the title to zero width.
function RemoveButton({ onRemove, disabled }: { onRemove: () => void; disabled: boolean }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button" disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onRemove(); }}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      title="Remove from tracker" aria-label="Remove from tracker"
      style={{
        flexShrink: 0, width: 26, height: 26, borderRadius: 6, border: "none",
        background: h ? T.errBg : "transparent", color: h ? T.err : T.t3,
        cursor: disabled ? "default" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.12s ease",
      }}
    >
      <X size={15} strokeWidth={2}/>
    </button>
  );
}

function TrackedRow({ app, index }: { app: TrackedApplication; index: number }) {
  const [h, setH] = useState(false);
  const rev = useReveal(index * 50, 8);
  const update = useUpdateApplication();
  const del    = useDeleteApplication();
  const score = app.score != null ? Math.round(app.score) : null;
  const appliedLabel = app.appliedAt
    ? new Date(app.appliedAt).toLocaleDateString("en-US", { day: "numeric", month: "short" })
    : null;
  const clickable = !!app.url;

  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={() => openPortal(app.url)}
      style={{
        ...rev, display: "flex", flexDirection: "column", gap: "12px",
        background: h ? T.s2 : T.s1, border: `1px solid ${h ? T.b2 : T.b0}`,
        borderRadius: "10px", padding: "14px 16px",
        transition: "background 0.15s ease, border-color 0.15s ease",
        cursor: clickable ? "pointer" : "default",
      }}
    >
      {/* top — score + title/company (full width) */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {score != null
          ? <ScoreRing score={score} size={40} showLabel={false} delay={index * 50}/>
          : <div style={{ width: 40, flexShrink: 0 }}/>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: T.display, fontSize: "14px", fontWeight: 600, color: T.t0,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: "2px" }}>
            {app.title}
          </div>
          <div style={{ fontFamily: T.body, fontSize: "11px", color: T.t2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {app.company}{app.location ? ` · ${app.location}` : ""}
            {appliedLabel && ` · applied ${appliedLabel}`}
          </div>
        </div>
        <RemoveButton onRemove={() => del.mutate(app.appId)} disabled={del.isPending} />
      </div>

      {/* bottom — status pipeline (click to advance via PATCH) + Open */}
      <Actions>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
          {PIPELINE.map((step) => {
            const active = app.status === step.status;
            return (
              <Badge
                key={step.status}
                variant={active ? (STATUS_VARIANT[step.status] ?? "info") : "ghost"}
                size="sm"
                style={{ cursor: active || update.isPending ? "default" : "pointer", opacity: active ? 1 : 0.6 }}
                {...(!active && !update.isPending && {
                  onClick: () => update.mutate({ appId: app.appId, status: step.status }),
                })}
              >
                {step.label}
              </Badge>
            );
          })}
          <div style={{ flex: 1 }}/>
          {app.url && (
            <Btn variant="outline" size="sm" icon={<ExternalLink size={13} strokeWidth={1.8}/>} onClick={() => openPortal(app.url)}>
              Open
            </Btn>
          )}
        </div>
      </Actions>
    </div>
  );
}

// ── Captured with the Chrome extension (private to this user) ────────────────
// Rows come from GET /api/captures (the private capture store), never from the shared job
// pool. Same status vocabulary as applications; PATCH / DELETE are scoped server-side.
interface CapturedJob {
  id: string; title: string; company: string; location: string | null; url: string; sourceSite: string;
  matchScore: number | null; scoreStatus: string; status: string; notes: string | null; createdAt: string;
}
const CAPTURE_PIPELINE: { status: string; label: string }[] = [
  { status: "saved", label: "Interested" }, ...PIPELINE.map((p) => ({ status: p.status as string, label: p.label })),
];
const SCORE_NOTE: Record<string, string> = {
  no_resume: "Not scored — you had no résumé when you captured this",
  no_embedding: "Not scored — your résumé was still being processed",
  embedding_failed: "Not scored — scoring was unavailable at capture time",
};

function useCaptures() {
  return useQuery({
    queryKey: ["captures"],
    queryFn: async () => (await axios.get<CapturedJob[]>("/api/captures")).data,
  });
}

function CapturedRow({ cap, index }: { cap: CapturedJob; index: number }) {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: async (status: string) => axios.patch(`/api/captures/${cap.id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["captures"] }),
  });
  const del = useMutation({
    mutationFn: async () => axios.delete(`/api/captures/${cap.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["captures"] }),
  });
  return (
    <Row url={cap.url} index={index}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {cap.matchScore != null
          ? <ScoreRing score={cap.matchScore} size={40} showLabel={false} delay={index * 50}/>
          : <div style={{ width: 40, flexShrink: 0 }}/>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: T.display, fontSize: "14px", fontWeight: 600, color: T.t0,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: "2px" }}>
            {cap.title}
          </div>
          <div style={{ fontFamily: T.body, fontSize: "11px", color: T.t2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {cap.company}{cap.location ? ` · ${cap.location}` : ""} · {cap.sourceSite}
          </div>
          {cap.matchScore == null && SCORE_NOTE[cap.scoreStatus] && (
            <div style={{ fontFamily: T.body, fontSize: "10px", color: T.t3, marginTop: "2px" }}>{SCORE_NOTE[cap.scoreStatus]}</div>
          )}
        </div>
        <RemoveButton onRemove={() => del.mutate()} disabled={del.isPending} />
      </div>
      <Actions>
        <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
          {CAPTURE_PIPELINE.map((step) => {
            const active = cap.status === step.status;
            return (
              <Badge
                key={step.status}
                variant={active ? (STATUS_VARIANT[step.status] ?? "info") : "ghost"}
                size="sm"
                style={{ cursor: active || update.isPending ? "default" : "pointer", opacity: active ? 1 : 0.6 }}
                {...(!active && !update.isPending && { onClick: () => update.mutate(step.status) })}
              >
                {step.label}
              </Badge>
            );
          })}
        </div>
      </Actions>
    </Row>
  );
}

// ── Queue row — a match you haven't applied to yet ───────────────────────────
function QueueRow({ match, index }: { match: MatchWithJob; index: number }) {
  const qc = useQueryClient();

  // Log a status against this job. Invalidates BOTH the queue and the tracked
  // list so a newly-applied job jumps up into "Your applications".
  const setStatus = useMutation({
    mutationFn: async (status: string) => {
      await axios.post("/api/applications", { jobId: match.jobId, matchId: match.matchId, status });
      if (status === "saved") track("job_saved");
      if (status === "applied") track("job_applied");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
  });

  const skip = useMutation({
    mutationFn: async () => {
      await axios.post("/api/applications", { jobId: match.jobId, matchId: match.matchId, status: "skipped" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
  });

  const score = Math.round(match.weightedScore);
  const daysAgo = match.postedAt
    ? Math.floor((Date.now() - new Date(match.postedAt).getTime()) / 86_400_000)
    : null;

  return (
    <Row url={match.url} index={index}>
      {/* Top: score ring + title/company/explanation (horizontal) */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <ScoreRing score={score} size={44} showLabel={false} delay={index * 50}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: T.display, fontSize: "14px", fontWeight: 600, color: T.t0,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: "2px" }}>
            {match.title}
          </div>
          <div style={{ fontFamily: T.body, fontSize: "11px", color: T.t2,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {match.company}{match.location ? ` · ${match.location}` : ""}
            {daysAgo !== null && ` · ${daysAgo === 0 ? "today" : `${daysAgo}d ago`}`}
          </div>
          {match.explanation && (
            <div style={{ fontFamily: T.body, fontSize: "11px", color: T.t1, marginTop: "4px",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <span style={{ color: T.tealSoft }}>↳ </span>{match.explanation}
            </div>
          )}
        </div>
      </div>

      {/* Bottom: action buttons */}
      <Actions>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          {match.url && (
            <Btn variant="outline" size="sm" icon={<ExternalLink size={13} strokeWidth={1.8}/>} onClick={() => openPortal(match.url)}>
              Open
            </Btn>
          )}
          <Btn variant="teal" size="sm" onClick={() => setStatus.mutate("applied")} disabled={setStatus.isPending}>
            Mark applied
          </Btn>
          <Btn variant="ghost" size="sm" onClick={() => skip.mutate()}>Skip</Btn>
        </div>
      </Actions>
    </Row>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3, letterSpacing: "0.12em",
      textTransform: "uppercase", marginBottom: "12px" }}>
      {children}
    </div>
  );
}

function ColumnHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
      <SectionLabel>{children}</SectionLabel>
      <div style={{ flex: 1, height: "1px", background: T.b0, marginBottom: "12px" }}/>
    </div>
  );
}

export function ApplyQueue() {
  const { data, isLoading } = useMatches({ minScore: 70, limit: 50 });
  const { data: apps }      = useApplications();
  const { data: captures }  = useCaptures();
  useEffect(() => { if ((captures ?? []).length > 0) trackOnce("extension_capture_viewed"); }, [captures]);

  // A job the user has ALREADY actioned (applied / skipped / saved / …) must
  // leave the to-apply queue — otherwise Mark-applied and Skip look like they did
  // nothing (the match row stays put, and a second click 409s on the unique
  // (user,job)). Filtering the queue by the applications set is what makes the
  // buttons visibly work: the card disappears, and applied ones show up on the right.
  const actionedJobIds = new Set((apps ?? []).map((a) => a.jobId));
  const tracked = (apps ?? []).filter((a) => TRACKED_STATUSES.has(a.status));
  const pending = (data ?? []).filter((m) => !actionedJobIds.has(m.jobId));
  const highFit = pending.filter(m => (m.weightedScore ?? 0) >= 85);
  const goodFit = pending.filter(m => (m.weightedScore ?? 0) >= 70 && (m.weightedScore ?? 0) < 85);
  const queueCount = highFit.length + goodFit.length;

  return (
    <div style={{ maxWidth: "1280px", margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: "20px" }}>
        <h1 style={{ fontFamily: T.display, fontSize: "26px", fontWeight: 700, color: T.t0, margin: "0 0 6px" }}>
          Application Tracker
        </h1>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0 }}>
          Track your applications manually. Caliber never applies on your behalf.
        </p>
      </div>

      {/* How-to banner */}
      <div style={{ background: T.s2, border: `1px solid ${T.b1}`, borderRadius: "8px",
        padding: "12px 16px", marginBottom: "24px",
        display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <span style={{ color: T.teal, fontSize: "14px", flexShrink: 0 }}>ℹ</span>
        <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t1, margin: 0, lineHeight: 1.6 }}>
          Open a role's portal, apply there, then click <strong style={{ color: T.t0 }}>Mark applied</strong> to move it into
          {" "}<strong style={{ color: T.t0 }}>Your applications</strong>, and advance its status as things progress.
        </p>
      </div>

      {/* ── Two columns: Your applications (tracker) | To apply (queue) ────── */}
      {/* row-reverse keeps desktop order [queue left][apps right] while on mobile
          (wrapped) the DOM order wins → apps appear on top, queue below. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "flex-start", flexDirection: "row-reverse" }}>

        {/* RIGHT (DOM first → mobile top) — Your applications */}
        <div style={{ flex: "2 1 340px", minWidth: 0 }}>
          <ColumnHeader>Your applications{tracked.length > 0 ? ` · ${tracked.length}` : ""}</ColumnHeader>

          {tracked.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {tracked.map((a, i) => <TrackedRow key={a.appId} app={a} index={i}/>)}
            </div>
          ) : (
            <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "10px",
              padding: "24px", textAlign: "center" }}>
              <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t2, margin: 0, lineHeight: 1.6 }}>
                Nothing tracked yet. Apply to a role on its portal, then hit
                {" "}<strong style={{ color: T.t0 }}>Mark applied</strong> — it'll show up here with its status.
              </p>
            </div>
          )}
          {/* Captured with the Chrome extension — private to this user */}
          {(captures ?? []).length > 0 && (
            <div style={{ marginTop: "20px" }} data-testid="captured-jobs">
              <ColumnHeader>Captured with the extension · {(captures ?? []).length}</ColumnHeader>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {(captures ?? []).map((c, i) => <CapturedRow key={c.id} cap={c} index={i}/>)}
              </div>
            </div>
          )}
        </div>

        {/* LEFT (DOM second → mobile bottom) — To apply */}
        <div style={{ flex: "3 1 440px", minWidth: 0 }}>
          <ColumnHeader>To apply{queueCount > 0 ? ` · ${queueCount}` : ""}</ColumnHeader>

          {isLoading && (
            <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t3 }}>Loading matches…</p>
          )}

          {highFit.length > 0 && (
            <>
              <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.tealSoft, letterSpacing: "0.10em",
                textTransform: "uppercase", margin: "0 0 8px 2px" }}>Strong fit · {highFit.length}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px" }}>
                {highFit.map((m, i) => <QueueRow key={m.matchId} match={m} index={i}/>)}
              </div>
            </>
          )}

          {goodFit.length > 0 && (
            <>
              <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em",
                textTransform: "uppercase", margin: "0 0 8px 2px" }}>Good fit · {goodFit.length}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {goodFit.map((m, i) => <QueueRow key={m.matchId} match={m} index={highFit.length + i}/>)}
              </div>
            </>
          )}

          {!isLoading && queueCount === 0 && (
            <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px",
              padding: "32px", textAlign: "center" }}>
              <p style={{ fontFamily: T.display, fontSize: "16px", color: T.t1, margin: "0 0 6px" }}>Queue is empty</p>
              <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0 }}>
                Matches scoring 70+ will appear here as they're scored.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Status legend */}
      <div style={{ marginTop: "28px", padding: "14px 16px", background: T.s1,
        border: `1px solid ${T.b0}`, borderRadius: "8px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.10em",
          textTransform: "uppercase", marginBottom: "10px" }}>
          Status flow
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {STATUS_OPTIONS.map((s, i) => (
            <div key={s.value} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Badge variant="default" size="sm">{s.label}</Badge>
              {i < STATUS_OPTIONS.length - 1 && <span style={{ color: T.t3, fontSize: "10px" }}>→</span>}
            </div>
          ))}
        </div>
        <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, margin: "8px 0 0", lineHeight: 1.5 }}>
          Each status is recorded for your reference only. No automated actions are taken.
        </p>
      </div>
    </div>
  );
}
